"use client";
import Image from "next/image";
import * as images from "@/components/images/Images";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import React from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import useDocumentLockScrollY from "@/hooks/useDocumentLockScrollY";
import { useToggle } from "@/hooks/useToggle";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useGetBoundingClientRect } from "@/hooks/useGetBoundingClientRect";
import { useSetGlobalCustomProperty } from "@/hooks/useSetGlobalCustomProperty";
import Icon from "@/components/ui/Icon";
import { HeaderProps } from "./types";
import { ROUTES } from "@/constants";
import Navigation from "./components/Navigation";
import NavigationMobile from "./components/NavigationMobile";

const Header = ({ menu, className }: HeaderProps) => {
  const [isMobileMenuOpened, toggleIsMobileMenuOpened, setIsMobileMenuOpened] =
    useToggle();
  const [isNavigationMenuOpened, , setIsNavigationMenuOpened] = useToggle();
  const { lockDocumentScrollY, unlockDocumentScrollY } =
    useDocumentLockScrollY();
  const isTablet = useMediaQuery("(min-width: 64rem)");
  const navigationMenuRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const scrollDir = useScrollDirection();
  const headerRef = useRef<HTMLElement>(null);
  const [headerClientRect] = useGetBoundingClientRect(headerRef);
  const menuItems = menu?.menuItems ?? [];

  const [activeSubIndex, setActiveSubIndex] = useState(0);
  const [activeSubIndexNone, setActiveSubIndexNone] = useState<number | null>(
    null
  );
  const [activeSubMenuTitle, setActiveSubMenuTitle] = useState("");

  useSetGlobalCustomProperty(
    "--main-header-height",
    `${Math.round(headerClientRect?.height ?? 0)}px`
  );

  const menuCloseHandler = useCallback(() => {
    setIsMobileMenuOpened(false);
    unlockDocumentScrollY();
  }, [setIsMobileMenuOpened, unlockDocumentScrollY]);

  useEffect(() => {
    const node = navigationMenuRef.current;

    if (!node || !isTablet) {
      setIsNavigationMenuOpened(false);

      observerRef.current?.disconnect();
      observerRef.current = null;
      return;
    }

    observerRef.current = new MutationObserver(() => {
      const openItem = node.querySelector('[data-state="open"]');

      if (openItem) {
        setIsNavigationMenuOpened(true);
      } else {
        setIsNavigationMenuOpened(false);
      }
    });

    observerRef.current.observe(node, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [isTablet]);

  useEffect(() => {
    if (isMobileMenuOpened) {
      lockDocumentScrollY();
    } else {
      unlockDocumentScrollY();
    }

    return () => {
      unlockDocumentScrollY();
    };
  }, [isMobileMenuOpened, lockDocumentScrollY, unlockDocumentScrollY]);

  useEffect(() => {
    if (isTablet) {
      setIsMobileMenuOpened(false);
      unlockDocumentScrollY();
    } else {
      setIsNavigationMenuOpened(false);
    }

    return () => {
      unlockDocumentScrollY();
      setIsNavigationMenuOpened(false);
    };
  }, [isTablet, setIsMobileMenuOpened, unlockDocumentScrollY]);

  useEffect(() => {
    const solutionsItem = menu?.menuItems?.find(
      (item) => item.link === "solutions"
    );

    if (solutionsItem?.submenuItems?.[0]) {
      setActiveSubIndex(0);
      setActiveSubMenuTitle(solutionsItem.submenuItems[0].title);
    }
  }, [menu]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 w-screen transform transition-transform duration-300 will-change-transform z-50",
        {
          "-translate-y-full": scrollDir === "down" && !isNavigationMenuOpened,
        },
        className
      )}
      ref={headerRef}
    >
      <div className="relative bg-white hidden lg:block z-50 px-6">
        <div className="container mx-auto py-2">
          <div className="flex items-center">
            <div className="flex">
              <Link href="/">
                <Image
                  src={images.logo}
                  alt="Languex logo"
                  width={149}
                  height={39}
                  className="w-[148] h-[38]"
                  priority
                />
              </Link>
            </div>
            <Navigation
              ref={navigationMenuRef}
              menuItems={menuItems}
              setActiveSubIndex={setActiveSubIndex}
              setActiveSubMenuTitle={setActiveSubMenuTitle}
              activeSubIndex={activeSubIndex}
              activeSubMenuTitle={activeSubMenuTitle}
            />

            <div className="flex gap-4">
              <Button variant="primary-outline" className="font-medium text-sm">
                <Link href={ROUTES.languexSignIn}>Log in</Link>
              </Button>
              <Button
                asChild
                variant="primary"
                className="font-medium text-sm border border-transparent w-[120]"
              >
                <Link href={ROUTES.startOrder}>Start Order</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* NOTE: Mobile menu */}
      <div className="z-[9999] bg-white lg:hidden">
        <div className="flex flex-auto min-w-0 justify-between relative p-6 py-3 z-20 bg-white shadow-md">
          <div className="flex">
            <Link href="/" onClick={menuCloseHandler}>
              <Image
                src={images.logo}
                alt="Languex logo"
                width={149}
                height={39}
                className="w-[124] h-[32]"
                priority
              />
            </Link>
          </div>
          <button
            onClick={toggleIsMobileMenuOpened}
            className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
            aria-label={isMobileMenuOpened ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMobileMenuOpened}
            aria-controls="mobile-navigation"
            title={isMobileMenuOpened ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpened ? (
              <X width={24} height={24} className="w-6 h-6 text-gray-800" />
            ) : (
              <Icon id="icon-menu_24" className="w-6 h-6 text-gray-800" />
            )}
          </button>
        </div>
        <div
          className={cn(
            "fixed !bg-white bg-opacity-95 z-10 inset-y-0 left-0 w-screen h-screen transform transition-transform duration-300 ease-in-out lg:hidden pointer-events-none",
            {
              "translate-x-0": isMobileMenuOpened,
              "-translate-x-full": !isMobileMenuOpened,
            }
          )}
          aria-hidden={true}
        />
        <div
          id="mobile-navigation"
          className={cn(
            "fixed inset-y-0 left-0 w-screen pt-[56] h-screen flex transform transition-transform duration-300 ease-in-out flex-col lg:hidden z-30 pointer-events-none",
            {
              "translate-x-0": isMobileMenuOpened,
              "-translate-x-full": !isMobileMenuOpened,
            }
          )}
        >
          <div className="w-full py-6 flex-auto pointer-events-auto overflow-y-auto">
            <NavigationMobile
              menuItems={menuItems}
              onMenuClose={menuCloseHandler}
              isMenuOpened={isMobileMenuOpened}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default memo(Header);
